import React, { useEffect, useRef, useState } from 'react';

/**
 * Cantidad tecleable de una línea de carrito.
 *
 * Petición de Kong (16-ago): "para socios que pidan 10 o 15 unidades es más
 * rápido escribirlo que darle 15 veces al botón". Convive con los botones −/+,
 * que siguen siendo el gesto cómodo para ajustar de uno en uno.
 *
 * Mientras se edita se guarda el texto EN CRUDO, así el campo se puede dejar
 * vacío un instante sin que la línea desaparezca; se confirma al salir o con
 * Enter y se descarta con Escape. Solo enteros positivos y hasta 4 dígitos: un
 * dedazo no mete 99.999 unidades. Un 0 o un vacío no borra la línea — para eso
 * está la X, que es explícita.
 */
const QtyInput = ({ value, onCommit, className = '', ariaLabel = 'Cantidad', decimales = false }) => {
  const [draft, setDraft] = useState(null);   // null = no se está editando
  // El borrador vive TAMBIÉN en una ref porque commit() corre desde el onBlur que
  // dispara el propio blur() de Escape: leyendo el estado veía todavía lo tecleado
  // (React aún no había aplicado el descarte) y lo confirmaba — Escape guardaba en
  // vez de cancelar (reporte del usuario, 17-ago). La ref es síncrona y no miente.
  const draftRef = useRef(null);
  const escribir = (v) => { draftRef.current = v; setDraft(v); };

  // Si la cantidad cambia POR FUERA (botones +/−, respuesta del servidor, otra
  // pantalla), el borrador que quedó en el campo deja de valer: manteniéndolo,
  // al salir se confirmaba y devolvía la cantidad a un valor anterior — el
  // "12 → 15 → 12" del vídeo del usuario (17-ago). Va por `escribir` para limpiar
  // la ref además del estado: si solo se limpiara el estado, commit() seguiría
  // viendo el borrador viejo en la ref y el salto atrás volvería.
  useEffect(() => { escribir(null); }, [value]);

  const commit = () => {
    const d = draftRef.current;
    if (d === null) return;
    // Con `decimales` (líneas a peso: 3,5 g de flor) se confirma con 3 decimales;
    // sin él, el entero de siempre — las cantidades por unidades no cambian.
    const n = decimales ? Number(parseFloat(d).toFixed(3)) : parseInt(d, 10);
    escribir(null);
    if (Number.isFinite(n) && n > 0 && n !== Number(value)) onCommit(n);
  };

  return (
    <input
      type="text"
      inputMode={decimales ? "decimal" : "numeric"}
      pattern={decimales ? undefined : "[0-9]*"}
      aria-label={ariaLabel}
      value={draft !== null ? draft : String(value)}
      onChange={(e) =>
        escribir(
          decimales
            ? e.target.value.replace(',', '.').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 8)
            : e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
        )
      }
      // Enfocar NO crea borrador: solo selecciona. Si lo creaba, bastaba con
      // tocar el campo y luego pulsar +/− para que al salir se confirmara el
      // número viejo y la cantidad saltara atrás (vídeo del usuario, 17-ago).
      // Ahora solo hay borrador si el staff ESCRIBE algo.
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        else if (e.key === 'Escape') { escribir(null); e.currentTarget.blur(); }
      }}
      onClick={(e) => e.stopPropagation()}
      className={className}
    />
  );
};

export default QtyInput;
